"use client"
import React, { memo, useState, useEffect, useRef } from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Stack from "@mui/material/Stack"
import IconButton from "@mui/material/IconButton"
import NextImage from "@src/components/NextImage"
import NextLink from "@src/components/NextLink"
import { ConfirmButton } from "@src/components/Button"
import Icons from "@src/components/Icons"
import PromotionBadges from "@src/components/PromotionBadges"
import { ROUTES } from "@src/constants/routes"
import ProductModel from "@src/models/product"
import { useLayoutContext } from "@src/layouts/provider"
import { ProductCardImage } from "@src/types/common"
import { useTranslation } from "react-i18next"
import FormattedPrice from "../FormattedPrice"
import UpcomingSalePanel from "@src/sections/product-detail/UpcomingSalePanel"
import { uniqueBy, urlPathname } from "@src/utils/unique"
import { Swiper, SwiperSlide } from "swiper/react"
import type { Swiper as SwiperInstance } from "swiper"

const ProductCard = memo(
  (props: {
    product: ProductModel
    onSelectedProduct: (product: ProductModel) => void
    isLiked: boolean
    mobileSalePanelBelowImage?: boolean
    toggleLike: (isLiked: boolean) => () => void
  }) => {
    const { t } = useTranslation()
    const { product, onSelectedProduct, isLiked, toggleLike, mobileSalePanelBelowImage = false } = props
    const { onSaleColor, currencyRates } = useLayoutContext()
    const {
      name,
      promotion_price: discountPrice,
      sale_price,
      is_new,
      discount_percent,
      image: productImage,
      images,
      slug,
      out_of_stock,
      is_best_seller,
      original_price: price,
      flash_sales: flashSales
    } = product || {}

    // 2026-10-12 presale: derive the presale signal once so the render
    // tree (badge + price block + CTA copy) all read from the same value.
    const upcomingFlashSale = React.useMemo(
      () =>
        (flashSales || []).find(
          s => s && s.status === "upcoming" && s.from && s.to
        ),
      [flashSales]
    )
    const hasUpcomingSale =
      !!upcomingFlashSale &&
      !out_of_stock &&
      !(flashSales || []).some(s => s.status === "active")
    const upcomingDiscountPercent = upcomingFlashSale
      ? Number(upcomingFlashSale.discount_percent || 0)
      : 0

    const [currentSubImage, setCurrentSubImage] = useState<string | null>(null)
    const [subImages, setSubImages] = useState<ProductCardImage[]>([])
    const imageSwiperRef = useRef<SwiperInstance | null>(null)
    const [imageCarouselAtStart, setImageCarouselAtStart] = useState(true)
    const [imageCarouselAtEnd, setImageCarouselAtEnd] = useState(false)
    const cardImages = subImages.length ? subImages : productImage?.url ? [{ small: productImage, large: productImage }] : []
    const { messages } = useLayoutContext()

    const handleBuyNow = () => {
      onSelectedProduct?.(product)
    }

    useEffect(() => {
      if (!product) return
      const list = Array.isArray(images) ? images : []
      setSubImages(
        uniqueBy(list, (image: ProductCardImage) =>
          urlPathname(image.large?.url || image.small?.url)
        )
      )
    }, [product, images])

    const badgeSx = {
      borderRadius: "4px",
      px: "8px",
      py: "4px",
      minHeight: "24px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center"
    }
    const badgeTextSx = {
      fontSize: "10px",
      lineHeight: 1.2,
      color: "#FFF",
      fontWeight: 700,
      whiteSpace: "nowrap" as const,
      letterSpacing: "0.02em"
    }

    const renderType = () => {
      if (out_of_stock) {
        return (
          <Box sx={{ ...badgeSx, backgroundColor: "#F46647" }}>
            <Typography sx={badgeTextSx}>{t("out_of_stock")}</Typography>
          </Box>
        )
      }

      if (hasUpcomingSale) return null

      if (product.promotion_price > 0) {
        return (
          <Box
            sx={{
              ...badgeSx,
              backgroundColor: onSaleColor || "#D8042C",
              minWidth: "52px"
            }}
          >
            <Typography sx={{ ...badgeTextSx, fontSize: "16px" }}>
              {Number(discount_percent).toFixed(0)}%
            </Typography>
          </Box>
        )
      }

      if (!is_best_seller && !is_new) {
        return null
      }

      return (
        <Stack direction="column" spacing={0.5} alignItems="flex-start">
          {is_new ? (
            <Box sx={{ ...badgeSx, backgroundColor: "#85A1FC" }}>
              <Typography sx={badgeTextSx}>{t("badge_new")}</Typography>
            </Box>
          ) : null}
          {is_best_seller ? (
            <Box sx={{ ...badgeSx, backgroundColor: "#8DC960" }}>
              <Typography sx={badgeTextSx}>{t("best_seller")}</Typography>
            </Box>
          ) : null}
        </Stack>
      )
    }

    return (
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          backgroundColor: "#FFFFFF",
          border: "1px solid #ECE9E9",
          boxShadow: "0 8px 22px rgba(26, 20, 20, .08)",
          borderRadius: "20px",
          padding: {
            xs: "8px",
            md: "14px"
          },
          position: "relative",
          display: "flex", flexDirection: "column", height: "100%"
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: {
              xs: 10,
              md: 18
            },
            left: {
              xs: 10,
              md: 18
            },
            zIndex: 1
          }}
        >
          {renderType()}
        </Box>
        <IconButton
          type="button"
          aria-label={isLiked ? t("remove_from_wishlist") : t("add_to_wishlist")}
          aria-pressed={!!isLiked}
          onClick={toggleLike(!isLiked)}
          sx={{
            position: "absolute",
            top: { xs: 16, md: 22 },
            right: { xs: 16, md: 22 },
            zIndex: 2,
            width: { xs: 36, md: 42 },
            height: { xs: 36, md: 42 },
            p: 0,
            borderRadius: "50%",
            border: isLiked ? "1px solid #F0C9CC" : "1px solid rgba(255,255,255,.9)",
            bgcolor: isLiked ? "#FFF0F1" : "rgba(255,255,255,.96)",
            color: isLiked ? "#B42D37" : "#47423F",
            boxShadow: "0 3px 12px rgba(35,28,24,.1)",
            transition: "background-color .2s, box-shadow .2s, transform .2s, color .2s",
            "&:hover": { bgcolor: "#FFF0F1", color: "#B42D37", transform: "translateY(-1px)", boxShadow: "0 5px 14px rgba(35,28,24,.15)" },
            "&:active": { transform: "scale(.92)" },
            "&:focus-visible": { outline: "2px solid #B42D37", outlineOffset: 3 },
            "& svg": { display: "block", animation: isLiked ? "productHeartPop .3s ease-out" : "none" },
            "@keyframes productHeartPop": { "0%": { transform: "scale(.8)" }, "60%": { transform: "scale(1.15)" }, "100%": { transform: "scale(1)" } },
            "@media (prefers-reduced-motion: reduce)": { transition: "none", "& svg": { animation: "none" }, "&:hover, &:active": { transform: "none" } }
          }}
        >
          <svg width="21" height="21" viewBox="0 0 24 24" fill={isLiked ? "currentColor" : "none"} aria-hidden="true">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </IconButton>
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "14px",
            backgroundColor: "#F0EFF2"
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: hasUpcomingSale ? 56 : undefined,
              bottom: hasUpcomingSale ? undefined : { xs: 8, md: 12 },
              right: {
                xs: 8,
                md: 12
              },
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.75
            }}
          >
            <PromotionBadges promotions={product?.promotions} max={2} />
          </Box>
          {hasUpcomingSale && price > 0 && (
            <Box
              sx={{
                position: "absolute",
                left: { xs: 5, md: 8 },
                right: { xs: 5, md: 8 },
                bottom: { xs: 5, md: 8 },
                zIndex: 2,
                ...(mobileSalePanelBelowImage && {
                  display: { xs: "none", md: "block" }
                })
              }}
            >
              <UpcomingSalePanel
                compact
                originalPrice={price}
                futurePrice={discountPrice ?? price}
                discountPercent={upcomingDiscountPercent}
                from={upcomingFlashSale!.from!}
                to={upcomingFlashSale!.to}
              />
            </Box>
          )}
          <NextLink
            href={`${ROUTES.PRODUCT}/${slug}`}
            sx={{ "& a": { width: "100%" } }}
          >
            <NextImage
              className="zoom-in"
              src={
                subImages?.[currentSubImage]?.large.url ||
                productImage.url ||
                ""
              }
              sx={{
                borderRadius: "14px",
                aspectRatio: "1 / 1",
                minHeight: { xs: 0, md: "200px" },
                "& .next-image-full": {
                  borderRadius: "14px",
                  cursor: "pointer",
                  objectFit: "cover"
                },
                width: "100%",
                cursor: "pointer"
              }}
              alt={name}
            />
          </NextLink>
        </Box>
        <Stack mt={{ xs: 1, md: 2 }} direction="column" spacing={{ xs: 0.75, md: 1.5 }} sx={{ flex: 1, px: 0.25 }}>
          <NextLink
            sx={{
              "& a": {
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                    fontSize: 14,
                    lineHeight: 1.35,
                    color: "#171717",
                    textDecoration: "none !important",
                    fontWeight: 400,
                    minHeight: "2.7em"
                  },
              "& a:hover": {
                color: "#F46647"
              }
            }}

            variant="body1"
            href={`${ROUTES.PRODUCT}/${slug}`}
          >
            {name}
          </NextLink>
          {hasUpcomingSale ? (
            <Box>
              {mobileSalePanelBelowImage && (
                <Box sx={{ display: { xs: "block", md: "none" } }}>
                  <UpcomingSalePanel cardDetails originalPrice={price} futurePrice={discountPrice ?? price} discountPercent={upcomingDiscountPercent} from={upcomingFlashSale!.from!} to={upcomingFlashSale!.to} />
                </Box>
              )}
            <Stack direction="row" alignItems="baseline" gap={0.75} flexWrap="wrap" sx={{ display: mobileSalePanelBelowImage ? { xs: "none", md: "flex" } : "flex" }}>
              <Typography component="span" sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 800, color: "#111111" }}>
                <FormattedPrice price={price} />
              </Typography>
            </Stack>
            </Box>
          ) : discountPrice ? (
            <Stack justifyItems="center" spacing={2}>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  fontSize: {
                    xs: 14,
                    md: 16
                  },
                  fontWeight: 700,
                  color: onSaleColor || "#D8042C"
                }}
              >
                <FormattedPrice price={discountPrice} />
              </Typography>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  fontSize: {
                    xs: 14,
                    md: 16
                  },
                  fontWeight: 600,
                  opacity: 0.5,
                  textDecoration: "line-through"
                }}
              >
                <FormattedPrice price={price} />
              </Typography>
            </Stack>
          ) : (
            <Stack justifyItems="center" spacing={2}>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  fontSize: {
                    xs: 14,
                    md: 16
                  },
                  fontWeight: 700
                }}
              >
                <FormattedPrice price={price} />
              </Typography>
            </Stack>
          )}

          {cardImages.length > 0 && (
            <Box sx={{ position: "relative", py: 0.5, px: "4px", mx: "-4px" }} aria-label="Chọn ảnh sản phẩm">
              <Swiper
                slidesPerView="auto"
                spaceBetween={8}
                onSwiper={swiper => {
                  imageSwiperRef.current = swiper
                  setImageCarouselAtStart(swiper.isBeginning)
                  setImageCarouselAtEnd(swiper.isEnd)
                }}
                onSlideChange={swiper => {
                  setImageCarouselAtStart(swiper.isBeginning)
                  setImageCarouselAtEnd(swiper.isEnd)
                }}
                style={{ padding: "6px 3px 7px" }}
              >
                {cardImages.map((image, index) => (
                  <SwiperSlide key={image.small?.url || index} style={{ width: 30 }}>
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Xem ảnh ${index + 1}`}
                      aria-pressed={Number(currentSubImage ?? 0) === index}
                      onClick={() => setCurrentSubImage(String(index))}
                      sx={{
                        width: 30,
                        height: 30,
                        display: "block",
                      p: 0,
                        borderRadius: "50%",
                        overflow: "hidden",
                        cursor: "pointer",
                      bgcolor: "transparent",
                      border: 0,
                      boxShadow: Number(currentSubImage ?? 0) === index ? "0 2px 8px rgba(36,29,25,.2)" : "0 1px 4px rgba(36,29,25,.12)",
                      transition: "box-shadow .18s, opacity .18s",
                      "&:hover": { opacity: 0.86, boxShadow: "0 2px 8px rgba(36,29,25,.2)" },
                      "&:active": { opacity: 0.75 },
                      "&:focus-visible": { outline: "none", boxShadow: "0 0 0 2px #B42D37" },
                    "@media (prefers-reduced-motion: reduce)": { transition: "none" }
                      }}
                    >
                      <NextImage src={image.small?.url} alt={`Ảnh ${index + 1}`} sx={{ width: "100%", height: "100%", "& .next-image-full": { borderRadius: "50%", objectFit: "cover" } }} />
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
              {cardImages.length > 4 && !imageCarouselAtStart && (
                <IconButton aria-label="Ảnh trước" onClick={() => imageSwiperRef.current?.slidePrev()} sx={{ position: "absolute", zIndex: 2, left: -2, top: "50%", transform: "translateY(-50%)", width: 25, height: 25, bgcolor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,.14)", "&:hover": { bgcolor: "#F7F3F0" } }}>
                  <Icons name="arrow-left" />
                </IconButton>
              )}
              {cardImages.length > 4 && !imageCarouselAtEnd && (
                <IconButton aria-label="Ảnh tiếp theo" onClick={() => imageSwiperRef.current?.slideNext()} sx={{ position: "absolute", zIndex: 2, right: -2, top: "50%", transform: "translateY(-50%)", width: 25, height: 25, bgcolor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,.14)", "&:hover": { bgcolor: "#F7F3F0" } }}>
                  <Icons name="arrow-right" />
                </IconButton>
              )}
            </Box>
          )}
          <Stack
            sx={{ mt: "auto !important", pt: { xs: 0.5, md: 1 } }}
            direction="row"
            spacing={1}
            alignItems="stretch"
          >
            <ConfirmButton
              sx={{
                  flex: 1,
                  width: "100%",
                  minWidth: "0 !important",
                  px: { xs: 1.5, md: 2 },
                  borderRadius: "999px",
                  backgroundColor: "#202020",
                  color: "#FFFFFF",
                  boxShadow: "none",
                  gap: 0.75,
                  transition: "background-color .2s, box-shadow .2s, transform .2s",
                  "&:hover": { backgroundColor: "#39312E", boxShadow: "0 4px 10px rgba(32,32,32,.15)", transform: "translateY(-1px)" },
                  "&:active": { transform: "translateY(0)", boxShadow: "none" },
                  "&:focus-visible": { outline: "2px solid #B42D37", outlineOffset: 3 },
                  "& .label": { fontSize: { xs: "12px !important", md: "14px !important" }, fontWeight: 600, letterSpacing: ".01em" },
                  "& .MuiButton-endIcon": { m: 0 },
                  "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } }
              }}
              height={44}
              heightMd={32}
              minHeightMd={32}
              type="button"
              label={t("buy_now")}
              endIcon={(
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M2 3h3l2.5 12h11l2-9H6M8 19a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm10 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              onClick={handleBuyNow}
            />
          </Stack>
        </Stack>
      </Box>
    )
  }
)

export default ProductCard
